import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBranch } from '@/features/branch/branch-context';
import { api } from '@/lib/api';

/**
 * Issuing branch on the MC. `issue_snapshot`: fixed at issue (address null if
 * Clinic setup was incomplete then). `current_setup_legacy`: an older MC with
 * no snapshot, showing the branch as configured now (not historical).
 */
export interface CertificateBranch {
  id: string;
  name: string;
  address: string[] | null;
  missing: string[];
  phone: string | null;
  email: string | null;
  mohRegistrationNo: string | null;
  source: 'issue_snapshot' | 'current_setup_legacy';
  capturedAt: string | null;
}

/** Browser print dialog, or the same sheet saved as PDF. */
export type CertificatePrintOutput = 'print' | 'pdf';

/** POST /medical-certificates/:id/print: a print request, not proof of output. */
export interface CertificatePrintRecord {
  number: string;
  output: CertificatePrintOutput;
  recorded: true;
}

/** GET /medical-certificates/:id: everything the printed MC shows. */
export interface CertificateDetail {
  id: string;
  number: string;
  status: 'valid' | 'revoked';
  issuedAt: string;
  from: string | null;
  to: string | null;
  days: number | null;
  doctor: { name: string; mmcNumber: string | null };
  issuedBy: { id: string; fullName: string };
  patient: {
    id: string;
    fullName: string;
    identity: {
      type: 'mykad' | 'passport';
      value: string;
      issuingCountry: string;
    } | null;
  };
  branch: CertificateBranch;
  verificationCode: string;
  scans: { count: number; lastAt: string | null };
  revoked: {
    at: string;
    by: { id: string; fullName: string } | null;
    reason: string | null;
  } | null;
  canRevoke: boolean;
}

/**
 * Kinds the public check answers for (API `PUBLIC_CHECK_KINDS`, RL-982/983).
 * consent_form has no public check (404 VERIFICATION_NOT_FOUND).
 */
export const CHECK_KINDS = [
  'medical_certificate',
  'time_slip',
  'referral_letter',
  'imaging_referral',
  'vaccination_certificate',
  'outside_prescription',
] as const;
export type CheckKind = (typeof CHECK_KINDS)[number];

export function isCheckKind(kind: string): kind is CheckKind {
  return (CHECK_KINDS as readonly string[]).includes(kind);
}

/**
 * GET /verify/:code (public): no patient, doctor or reason. Every kind has
 * the reference, status, issue date and issuing clinic; only an MC adds
 * `from`, `to` and `days`.
 */
export interface CertificateCheck {
  reference: string;
  /** A CheckKind today; any other value is shown as a generic document. */
  kind: string;
  status: 'valid' | 'revoked';
  revokedAt: string | null;
  issuedAt: string;
  /** medical_certificate only. */
  from?: string | null;
  to?: string | null;
  days?: number | null;
  branch: { name: string; address: string[] | null };
}

export function useCertificate(id: string | null) {
  const { selected } = useBranch();
  return useQuery({
    queryKey: ['medical-certificate', selected?.id ?? 'all', id],
    enabled: !!id,
    queryFn: ({ signal }) =>
      api
        .get<CertificateDetail>(`/medical-certificates/${id}`, { signal })
        .then((r) => r.data),
  });
}

export function useRevokeCertificate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api
        .post<CertificateDetail>(`/medical-certificates/${id}/revoke`, {
          reason,
        })
        .then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['medical-certificates'] });
      void qc.invalidateQueries({ queryKey: ['medical-certificate'] });
    },
  });
}

/** Audits a print/PDF request; call once per user action, before printing. */
export function useRecordCertificatePrint() {
  return useMutation({
    mutationFn: ({
      id,
      output,
    }: {
      id: string;
      output: CertificatePrintOutput;
    }) =>
      api
        .post<CertificatePrintRecord>(`/medical-certificates/${id}/print`, {
          output,
        })
        .then((r) => r.data),
  });
}

export function useCertificateCheck(code: string) {
  return useQuery({
    queryKey: ['certificate-check', code],
    retry: false,
    queryFn: ({ signal }) =>
      api
        .get<CertificateCheck>(`/verify/${encodeURIComponent(code)}`, {
          signal,
        })
        .then((r) => r.data),
  });
}
